import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import { CustomerDiscount } from '../../api/customerDiscounts';

interface CouponDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  coupon: CustomerDiscount | null;
}

const CouponDetailsDialog: React.FC<CouponDetailsDialogProps> = ({
  open,
  onClose,
  coupon,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');

  useEffect(() => {
    if (coupon && open) {
      const qrData = JSON.stringify({
        code: coupon.code,
        id: coupon.id,
        type: 'coupon',
        timestamp: new Date().toISOString(),
      });

      QRCode.toDataURL(qrData, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      })
        .then((dataUrl) => {
          setQrCodeDataUrl(dataUrl);
        })
        .catch((err) => {
          console.error('Error generating QR code:', err);
        });
    }
  }, [coupon, open]);

  // Format date range
  const formatDateRange = () => {
    if (!coupon) return t('coupons.noExpiry');
    
    if (!coupon.startsAt && !coupon.expiresAt) {
      return t('coupons.noExpiry');
    }
    
    const startDate = coupon.startsAt ? new Date(coupon.startsAt) : null;
    const endDate = coupon.expiresAt ? new Date(coupon.expiresAt) : null;
    
    if (startDate && endDate) {
      return `${startDate.toLocaleDateString(i18n.language)} - ${endDate.toLocaleDateString(i18n.language)}`;
    } else if (startDate) {
      return `${startDate.toLocaleDateString(i18n.language)} - ${t('coupons.noExpiry')}`;
    } else if (endDate) {
      return `${t('coupons.noStartDate')} - ${endDate.toLocaleDateString(i18n.language)}`;
    }
    
    return t('coupons.noExpiry');
  };

  if (!coupon) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth={!isMobile}
      PaperProps={{
        sx: {
          backgroundColor: '#F5F5F5',
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
          margin: isMobile ? 0 : 'auto',
        },
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e0e0e0',
        pb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#F5F5F5',
        px: 3,
        pt: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isMobile && (
            <IconButton
              onClick={onClose}
              sx={{
                color: '#B3907A',
                mr: 1,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            sx={{
              color: '#B3907A',
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            {t('coupons.giftVoucherDetails')}
          </Typography>
        </Box>
        {!isMobile && (
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, backgroundColor: '#F5F5F5' }}>
          {/* QR Code */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 4,
              pt: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                p: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              {qrCodeDataUrl ? (
                <img
                  src={qrCodeDataUrl}
                  alt="QR Code"
                  style={{
                    width: 200,
                    height: 200,
                    display: 'block',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 200,
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                  }}
                >
                  <Typography color="text.secondary">
                    {t('coupons.loadingQR')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Voucher Information */}
          <Box sx={{ mb: 4 }}>
            {/* Valid Period */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  color: '#9CA3AF',
                  fontSize: '14px',
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {t('coupons.validPeriod')}
              </Typography>
              <Typography
                sx={{
                  color: '#000000',
                  fontSize: '16px',
                  fontWeight: 400,
                }}
              >
                {formatDateRange()}
              </Typography>
            </Box>

            {/* Promotion Name */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  color: '#9CA3AF',
                  fontSize: '14px',
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {t('coupons.promotionName')}
              </Typography>
              <Typography
                sx={{
                  color: '#000000',
                  fontSize: '16px',
                  fontWeight: 400,
                }}
              >
                {coupon.promotionName || coupon.name || '-'}
              </Typography>
            </Box>

            {/* Promotion Description */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  color: '#9CA3AF',
                  fontSize: '14px',
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {t('coupons.promotionDescription')}
              </Typography>
              <Typography
                sx={{
                  color: '#000000',
                  fontSize: '16px',
                  fontWeight: 400,
                }}
              >
                {coupon.promotionDescription || coupon.description || '-'}
              </Typography>
            </Box>

            {/* Important Notice */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  color: '#9CA3AF',
                  fontSize: '14px',
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {t('coupons.importantNotice')}
              </Typography>
              <Typography
                sx={{
                  color: '#000000',
                  fontSize: '16px',
                  fontWeight: 400,
                }}
              >
                {coupon.importantNotice || '-'}
              </Typography>
            </Box>
          </Box>

          {/* Redeem Button */}
          <Box
            sx={{
              mt: 4,
              ...(isMobile && {
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                p: 3,
                backgroundColor: '#F5F5F5',
              }),
            }}
          >
            <Button
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: '#B3907A',
                color: 'white',
                fontWeight: 600,
                fontSize: '16px',
                py: 2,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#A07D6B',
                },
              }}
              onClick={() => {
                // Handle redeem action - could navigate to store locator or show instructions
              }}
            >
              {t('coupons.redeemInStore')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    );
  };

export default CouponDetailsDialog;
